import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";
import { PageHero } from "./About";
import { PROJECTS_COMPLETED, PROJECTS_ONGOING } from "../data/site";

const FILTERS = ["All", "Completed", "Ongoing"];

const Projects = () => {
    const [filter, setFilter] = useState("All");

    const all = useMemo(
        () => [
            ...PROJECTS_COMPLETED.map((p) => ({ ...p, status: "Completed" })),
            ...PROJECTS_ONGOING.map((p) => ({ ...p, status: "Ongoing" })),
        ],
        []
    );

    const filtered = filter === "All" ? all : all.filter((p) => p.status === filter);

    return (
        <>
            <PageHero
                eyebrow="Our Portfolio"
                title="Projects delivered. Trust earned."
                subtitle="Industrial factories, hospitals, schools, government buildings, dairy facilities, and residential towers — explore systems we've designed, executed, and continue to maintain."
            />

            <section className="section" data-testid="projects-section">
                <div className="container-vil">
                    <div className="flex flex-wrap items-center justify-between gap-6 mb-12">
                        <div className="flex gap-2 flex-wrap">
                            {FILTERS.map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    data-testid={`project-filter-${f.toLowerCase()}`}
                                    className={`px-5 py-2.5 text-sm font-mono uppercase tracking-widest transition border ${
                                        filter === f
                                            ? "bg-[#E53935] border-[#E53935] text-slate-900"
                                            : "bg-transparent border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-900"
                                    }`}
                                >
                                    {f}
                                    <span className="ml-2 text-xs opacity-60">
                                        {f === "All"
                                            ? all.length
                                            : f === "Completed"
                                              ? PROJECTS_COMPLETED.length
                                              : PROJECTS_ONGOING.length}
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div className="text-sm text-slate-500 font-mono">
                            Showing {filtered.length} project{filtered.length === 1 ? "" : "s"}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filtered.map((p, i) => (
                            <ProjectCard key={`${p.name}-${i}`} project={p} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            <section
                className="section relative overflow-hidden"
                style={{ background: "#f7f7f8" }}
                data-testid="projects-cta"
            >
                <div className="red-glow left-1/3 top-1/2" />
                <div className="container-vil text-center relative z-10">
                    <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl max-w-3xl mx-auto leading-tight">
                        Want VIL on your next project?
                    </h2>
                    <p className="text-slate-600 mt-6 max-w-xl mx-auto">
                        From single-system installations to multi-phase EPC packages — we
                        scale to match your build timeline.
                    </p>
                    <Link to="/contact" className="btn-primary mt-8 inline-flex" data-testid="projects-cta-btn">
                        Discuss Your Project <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </>
    );
};

const ProjectCard = ({ project, index }) => {
    const isOngoing = project.status === "Ongoing";
    return (
        <div
            className="group glass corner-accent relative overflow-hidden card-lift"
            data-testid={`project-card-${index}`}
        >
            <div className="relative h-56 overflow-hidden">
                <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-cover img-zoom"
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(0deg, rgba(5,5,5,0.95) 0%, rgba(5,5,5,0.2) 70%)",
                    }}
                />
                <div className="absolute top-4 left-4">
                    <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-mono uppercase tracking-widest border ${
                            isOngoing
                                ? "bg-[#E53935]/15 border-[#E53935]/50 text-[#FF8A80]"
                                : "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                        }`}
                    >
                        {isOngoing ? <Clock className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                        {project.status}
                    </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="text-xs font-mono text-white uppercase tracking-widest mb-1">
                        {project.type}
                    </div>
                    <h3 className="font-display font-bold text-lg leading-tight" style={{ color: 'white' }}>
                        {project.name}
                    </h3>
                </div>
            </div>
            <div className="p-5 flex items-center justify-between border-t border-slate-100">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <MapPin className="w-4 h-4 text-[#E53935]" />
                    {project.location}
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-[#E53935] transition" />
            </div>
        </div>
    );
};

export default Projects;
